import { alpha, darken, lighten, Theme } from "@mui/material/styles";

/**
 * Visual state of a single piece of data in the inline-edit views.
 *  - idle:  saved value, nothing pending.
 *  - dirty: edited by the user and not saved yet (app yellow, bold).
 *  - saved: saved during this visit (green).
 *  - error: failed validation.
 * `auto` (blue) is orthogonal: the value is calculated / auto-filled.
 */
export type FieldState = "idle" | "dirty" | "saved" | "error";

export interface FieldTone {
  bg: string;
  border: string;
  text: string;
  accent: string;
}

/** Readable text shade for an accent color on the current surface. */
const ink = (theme: Theme, color: string, amount: number): string =>
  theme.palette.mode === "dark"
    ? lighten(color, amount * 0.6)
    : darken(color, amount);

export const getFieldTone = (
  theme: Theme,
  state: FieldState,
  auto = false
): FieldTone => {
  const { warning, success, info, error } = theme.palette;
  switch (state) {
    case "dirty":
      return {
        bg: alpha(warning.main, 0.14),
        border: warning.main,
        text: ink(theme, warning.main, 0.55),
        accent: warning.main,
      };
    case "saved":
      return {
        bg: alpha(success.main, 0.08),
        border: alpha(success.main, 0.55),
        text: ink(theme, success.main, 0.05),
        accent: success.main,
      };
    case "error":
      return {
        bg: alpha(error.main, 0.08),
        border: error.main,
        text: theme.palette.text.primary,
        accent: error.main,
      };
    default:
      return auto
        ? {
            bg: alpha(info.main, 0.06),
            border: alpha(info.main, 0.28),
            text: ink(theme, info.main, 0.2),
            accent: info.main,
          }
        : {
            bg: "transparent",
            border: "transparent",
            text: theme.palette.text.primary,
            accent: theme.palette.text.secondary,
          };
  }
};
